/**
 * ChakraEventHelper - Universal chakra event extraction helper
 *
 * DRY principle: Single source of truth for extracting boolean values from chakra events
 * KISS principle: Simple, focused helper with clear API
 *
 * Problem solved: event.showValue || event.data?.showValue doesn't work with false
 * Solution: Use nullish coalescing operator (??) to handle false correctly
 */

/**
 * Extract boolean value from chakra toggle event
 * Handles both event-level and data-level properties
 *
 * @param event - Chakra toggle event
 * @param propertyName - Property name (e.g., 'showMuhurtaSectors', 'showPada')
 * @returns boolean value (defaults to false if not found)
 */
export function extractBooleanFromEvent(event: any, propertyName: string): boolean {
    // Try data level first, then event level, default to false
    return event.data?.[propertyName] ?? event[propertyName] ?? false;
}

/**
 * Extract chart ID from event
 *
 * @param event - Event object
 * @returns Chart ID or null
 */
export function extractChartIdFromEvent(event: any): string | null {
    return event.chartId || event.data?.chartId || null;
}

/**
 * Check if event targets specific chart
 *
 * @param event - Event object
 * @param targetChartId - Target chart ID
 * @returns true if event is for this chart (or broadcast to all)
 */
export function isEventForChart(event: any, targetChartId: string): boolean {
    const eventChartId = extractChartIdFromEvent(event);
    // No chartId means broadcast to all charts
    return !eventChartId || eventChartId === targetChartId;
}